import React, { useMemo, useState } from "react";

import {
  Typography,
  Button,
  Skeleton,
  Alert,
  Grid,
  Divider,
} from "@mui/material";
import { ErrorOutlineOutlined, PictureAsPdf } from "@mui/icons-material";
import { ptBR } from "date-fns/locale";

import { BlockStandard } from "components/admin/BlockStandard";
import { DataTable, DataTableProps } from "components/dashboard/DataTable";
import { CollapseSection } from "components/dashboard/CollapseSection";
import { DiscountAlert } from "components/dashboard/DiscountAlert";
import { ButtonLink } from "components/MUIExtensions/ButtonLink";
import { InvoiceApprovalAlert } from "components/dashboard/InvoiceApprovalAlert";
import { useMonthlyBillsContext } from "contexts/BillingContext";
import {
  parseConsumption,
  parseCredits,
  parseEconomy,
  parseGreenEnergy,
} from "logic/MonthlyBilling";
import { PDF_DOWNLOAD } from "featureSwitches/flagsList";

export const MonthlyBilling = () => {
  const { isLoading, monthData, downloadInvoicePDF, isDownloading } =
    useMonthlyBillsContext();
  const [blobURL, setBlobURL] = useState<string>();

  const isProsumer = monthData?.isProsumer || false;

  const { economy, consumption, credits, greenEnergyExplanation } =
    useMemo((): Record<
      "economy" | "consumption" | "credits" | "greenEnergyExplanation",
      DataTableProps["data"]
    > => {
      const isProsumer = monthData?.isProsumer || false;
      const economy = parseEconomy(monthData, isProsumer);
      const consumption = parseConsumption(isProsumer, monthData);
      const credits = parseCredits(monthData);
      const greenEnergyExplanation = !isProsumer
        ? parseGreenEnergy(monthData)
        : [];

      return { economy, consumption, credits, greenEnergyExplanation };
    }, [monthData]);

  const displayInvoiceApproval =
    !isLoading &&
    (monthData?.invoiceApproval.status === "CONSISTENT" ||
      monthData?.invoiceApproval.status === "INCONSISTENT");

  const handleDownload = async (invoiceUrl: string) => {
    if (blobURL) {
      URL.revokeObjectURL(blobURL);
    }

    // @ts-expect-error downloadInvoicePDF will never be undefined here
    const newBlob = await downloadInvoicePDF(invoiceUrl);
    setBlobURL(newBlob);
    window.open(newBlob);
  };

  return (
    <>
      <BlockStandard>
        {!isProsumer &&
          (monthData?.finalBillingAmount === 0 ||
            monthData?.generationChargeBrl === 0) && (
            <Alert
              icon={<ErrorOutlineOutlined />}
              color="error"
              variant="outlined"
            >
              <Typography variant="body1" color="text.primary" fontWeight={500}>
                {
                  "Ainda não existem dados suficientes no sistema para este mês."
                }
              </Typography>
            </Alert>
          )}

        <DataTable data={economy} isLoading={isLoading} />

        {!isProsumer && (
          <CollapseSection title="Entendendo o cálculo da energia verde">
            <Grid container gap={1}>
              {greenEnergyExplanation.map(({ key, value, tooltipText }) => (
                <Grid key={key} container gap={1}>
                  <Grid
                    container
                    direction="row"
                    justifyContent="space-between"
                  >
                    <Typography variant="body2">{key}</Typography>
                    <Typography>{value}</Typography>
                  </Grid>
                  <Typography variant="caption">{tooltipText}</Typography>
                  <Divider sx={{ width: "100%" }} />
                </Grid>
              ))}
            </Grid>
          </CollapseSection>
        )}

        {/* if it's still loading, DiscountAlert will display the Skeleton */}
        {/* if I have loaded and don't have monitoring but I also don't have the
         data, something is wrong and I'll hide the component */}

        {(isLoading || isProsumer) && (
          <DiscountAlert discount={monthData?.discount} isLoading={isLoading} />
        )}
        {!isLoading &&
          !isProsumer &&
          !!monthData?.generationEconomyPercentage && (
            <Alert variant="outlined" color="success">
              <Typography variant="body2">
                Você economizou
                <Typography variant="h2" component="span">
                  {` ${(
                    monthData.generationEconomyPercentage / 100
                  ).toLocaleString(ptBR.code, {
                    style: "percent",
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  })} `}
                </Typography>
                em gastos com energia esse mês.
              </Typography>
            </Alert>
          )}

        {isProsumer && (
          <CollapseSection
            title="Como analisar minha fatura e saber se meu consumo aumentou?"
            text="Examine sua fatura, comparando o consumo atual com períodos anteriores. Leve em conta variações sazonais (estação do ano, chuvas, incidência solar) e fatores externos (novos aparelhos, como ar condicionado de alto consumo)."
          />
        )}
      </BlockStandard>

      {!isLoading &&
        PDF_DOWNLOAD.getValue() &&
        monthData?.invoicesDownloadUrls && (
          <BlockStandard title="Ver PDFs das faturas:">
            <Grid item container gap={2} justifyContent="space-around">
              {monthData.invoicesDownloadUrls.map((invoiceUrl, i) => (
                <Button
                  key={invoiceUrl}
                  onClick={() => handleDownload(invoiceUrl)}
                  startIcon={<PictureAsPdf />}
                  variant="outlined"
                  disabled={isDownloading}
                >
                  <Typography variant="subtitle1">
                    {isDownloading ? (
                      <Skeleton width={200} />
                    ) : (
                      `Fatura ${i + 1}`
                    )}
                  </Typography>
                </Button>
              ))}
            </Grid>
          </BlockStandard>
        )}

      {displayInvoiceApproval && (
        <BlockStandard title="Análise de Fatura">
          <InvoiceApprovalAlert
            status={monthData?.invoiceApproval.status}
            isLoading={isLoading}
          />
          <Typography variant="body1">
            A Enermatch analisou os mínimos detalhes da sua fatura de energia
            para te ajudar a ter mais controle do seu sistema de energia.
          </Typography>

          <ButtonLink to="/admin/billing/invoice-detail" variant="contained">
            <Typography variant="subtitle1" color="text.secondary">
              Ver análise detalhada da minha fatura
            </Typography>
          </ButtonLink>
        </BlockStandard>
      )}
      <BlockStandard title={isProsumer ? "Consumo x Geração" : "Consumo"}>
        <DataTable data={consumption} isLoading={isLoading} />
        {isProsumer && (
          <CollapseSection
            title="Como saber o momento de aumentar o meu sistema solar?"
            text="Para considerar aumentar o seu sistema solar, leve em consideração o seu consumo atual, se houve aumento das suas necessidade de energia, e a eficiência do seu sistema solar atual."
          />
        )}
      </BlockStandard>

      <BlockStandard hideDivider title="Meus Créditos">
        <Typography variant="body1">
          Créditos de um sistema solar se referem à geração excedente de
          energia, que foi convertida em um saldo de créditos. Saber mais
        </Typography>
        <DataTable data={credits} isLoading={isLoading} />
      </BlockStandard>
    </>
  );
};
